import React, { ComponentType, useCallback } from "react";
import { Platform } from "react-native";
import { useRoutes as reactRouterUseRoutes } from "react-router";
import { OnI18nErrorFn } from "@lookiero/i18n-react";
import { QueryBus } from "@lookiero/messaging";
import { MessagingRoot } from "@lookiero/messaging-react/bootstrap";
import { Locale } from "@lookiero/sty-psp-locale";
import {
  SentryEnvironment,
  SentryLoggerFunctionArgs,
  sentryLogger,
  sentryLoggerHOC,
} from "@lookiero/sty-psp-logging/sentry";
import { Layout } from "@lookiero/sty-psp-ui";
import { Tradename } from "@lookiero/sty-sp-tradename";
import { Customer } from "../../projection/customer/customer";
import { KameleoonEnvironment } from "../ab-testing/kameleoonEnvironment";
import { OnSuccessFunction as OnCheckoutFlowSuccessFunction } from "./hooks/useCheckoutFlow";
import { QueryBusProvider } from "./hooks/useQueryBus";
import { i18n } from "./i18n/i18n";
import { Routing } from "./routing/Routing";

interface RootFunctionArgs {
  readonly Messaging: MessagingRoot;
  readonly development?: boolean;
  readonly queryBus: QueryBus;
  readonly sentry: () => SentryEnvironment;
  readonly getAuthToken: () => Promise<string>;
  readonly kameleoon: () => KameleoonEnvironment;
}

interface RootFunction {
  (args: RootFunctionArgs): (args: SentryLoggerFunctionArgs) => ComponentType<RootProps>;
}

interface RootProps {
  readonly basePath: string;
  readonly locale: Locale;
  readonly customer: Customer;
  readonly layout: Layout;
  readonly tradename: Tradename;
  readonly onNotAccessible: () => void;
  readonly onCheckoutFlowSuccess: OnCheckoutFlowSuccessFunction;
  readonly useRedirect: () => Record<string, string>;
  readonly useRoutes?: typeof reactRouterUseRoutes;
}

const root: RootFunction = ({ Messaging, queryBus, getAuthToken, development, sentry, kameleoon: kameleoonConfig }) => {
  const logger = sentryLogger(sentry);
  const kameleoon = kameleoonConfig();

  const Root = ({
    basePath,
    locale,
    customer,
    layout,
    tradename,
    onNotAccessible,
    onCheckoutFlowSuccess,
    useRedirect,
    useRoutes = reactRouterUseRoutes,
  }: RootProps) => {
    const handleOnI18nError = useCallback<OnI18nErrorFn>(
      (error, fingerprint) =>
        logger.captureException(error, fingerprint ? { captureContext: { fingerprint: [...fingerprint] } } : undefined),
      [],
    );

    return (
      <Messaging includeReactQueryDevTools={Platform.OS === "web"}>
        <QueryBusProvider queryBus={queryBus}>
          <Routing
            I18n={i18n({ tradename })}
            basePath={basePath}
            customer={customer}
            getAuthToken={getAuthToken}
            kameleoon={kameleoon}
            layout={layout}
            locale={locale}
            tradename={tradename}
            useRedirect={useRedirect}
            useRoutes={useRoutes}
            onCheckoutFlowSuccess={onCheckoutFlowSuccess}
            onI18nError={development ? undefined : handleOnI18nError}
            onNotAccessible={onNotAccessible}
          />
        </QueryBusProvider>
      </Messaging>
    );
  };

  const hoc = sentryLoggerHOC({ logger });

  /**
   * It's important to not call sentryLoggerHOC each time this function gets called.
   * That's why its called outside this method.
   */
  return ({ customerId }) => hoc({ customerId })(Root);
};

export type { RootProps };
export { root };
